import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MobileAlertContext } from './MobileAlertContext';
import MobileAlertStack from './MobileAlertStack';

const normalizeActions = (actions, alertId) => {
  if (!Array.isArray(actions)) {
    return [];
  }

  return actions.map((action, index) => ({
    id: `${alertId}-action-${index}`,
    label: action?.label ?? 'Action',
    onClick: typeof action?.onClick === 'function' ? action.onClick : null,
    variant: action?.variant || 'secondary',
    dismissOnClick: action?.dismissOnClick !== false
  }));
};

export const MobileAlertProvider = ({ children }) => {
  const idCounterRef = useRef(0);
  const [alerts, setAlerts] = useState([]);

  const dismiss = useCallback((id) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clear = useCallback(() => {
    setAlerts([]);
  }, []);

  const notify = useCallback((options = {}) => {
    const id = `mobile-alert-${Date.now()}-${idCounterRef.current++}`;
    setAlerts((prev) => [
      ...prev,
      {
        id,
        variant: options.variant || 'info',
        title: options.title || '',
        description: options.description || '',
        dismissible: options.dismissible !== false,
        duration: typeof options.duration === 'number' ? options.duration : null,
        actions: normalizeActions(options.actions, id)
      }
    ]);

    return id;
  }, []);

  const confirm = useCallback((options = {}) => {
    const {
      title = '',
      description = '',
      confirmText = 'Conferma',
      cancelText = 'Annulla',
      variant = 'warning',
      onConfirm,
      onCancel
    } = options;

    return new Promise((resolve) => {
      let alertId = null;

      const handleResult = (result, callback) => {
        if (typeof callback === 'function') {
          callback();
        }

        resolve(result);

        if (alertId) {
          dismiss(alertId);
        }
      };

      alertId = notify({
        variant,
        title,
        description,
        dismissible: false,
        actions: [
          {
            label: cancelText,
            variant: 'ghost',
            onClick: () => handleResult(false, onCancel)
          },
          {
            label: confirmText,
            variant: 'primary',
            onClick: () => handleResult(true, onConfirm)
          }
        ]
      });
    });
  }, [dismiss, notify]);

  const value = useMemo(() => ({
    notify,
    dismiss,
    clear,
    confirm
  }), [notify, dismiss, clear, confirm]);

  return (
    <MobileAlertContext.Provider value={value}>
      {children}
      <MobileAlertStack alerts={alerts} onDismiss={dismiss} />
    </MobileAlertContext.Provider>
  );
};
